import React, { useState } from "react"
import { Link } from "react-router-dom"
import { Swiper, SwiperSlide } from "swiper/react"
import { Autoplay, EffectFade } from "swiper/modules"
import { motion, useScroll, useTransform } from "framer-motion"
import "swiper/css"
import "swiper/css/effect-fade"
import About from "../Components/About/About"
import Path from "../Components/Path/Path"
import Programes from "../Components/Programes/Programes"
import Blog from "../Components/Blog/Blog"
import Gallery from "../Components/Gallery/Gallery"
import Footer from "../Components/Outlets/Footer"
import Nav from "../Components/Outlets/Nav"
import slide1 from "../Assets/hero1.jpg"
import slide2 from "../Assets/hero2.jpg"
import slide3 from "../Assets/hero3.png"

const heroSlides = [slide1, slide2, slide3]

const faqs = [
	{
		q: "Why Le Fille Global School?",
		a: "Le Fille Global School is an exclusive girls-focused career school that combines academic excellence with entrance exam coaching (NEET, JEE, KEAM, CUET), professional courses, and global university pathways — all in one safe and empowering institution."
	},
	{
		q: "What entrance exams do you coach for?",
		a: "We offer integrated coaching for NEET, JEE (Main & Advanced), KEAM, CUET, and other national-level entrance exams — structured alongside the regular +1 & +2 academic curriculum."
	},
	{
		q: "Do you offer global university pathways?",
		a: "Yes! We provide international admissions counseling, scholarship guidance, and personality & profile development to help students secure admissions in top global universities."
	},
	{
		q: "Is this school exclusively for girls?",
		a: "Yes. Le Fille Global School is designed exclusively for young women, providing a focused, safe, and empowering environment that promotes confidence, leadership, and academic excellence."
	}
]

const HomePage = () => {
	const [openFaq, setOpenFaq] = useState(0)
	const toggleFaq = (idx) => setOpenFaq(openFaq === idx ? -1 : idx)

	const { scrollYProgress } = useScroll()
	const y = useTransform(scrollYProgress, [0, 1], [0, -200])

	return (
		<div>
			<Nav />
			<div className="page-main">
				<div className="hero-slider-wrap">
					<Swiper
						modules={[Autoplay, EffectFade]}
						effect="fade"
						autoplay={{ delay: 4500, disableOnInteraction: false }}
						loop={true}
						speed={1200}
						className="hero-swiper"
					>
						{heroSlides.map((img, i) => (
							<SwiperSlide key={i}>
								<motion.div 
									className="hero-slide" 
									style={{ backgroundImage: `url(${img})`, y: i === 0 ? y : 0 }} 
								/>
							</SwiperSlide>
						))}
					</Swiper>
					<div className="hero-content-layer">
						<div className="container-main">
							<motion.div 
								className="home-page-content"
								initial={{ opacity: 0, y: 30 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.8 }}
							>
								<h1 className="home-main-heading montserrat">A Global Career School <span className="montserrat block-text">for Young Women</span></h1>
								<p className="home-sub-text">Le Fille Global School offers integrated +1 &amp; +2 education with coaching for NEET, JEE, KEAM, CUET, and national entrance exams&mdash;along with professional and global career pathways, exclusively for girls.</p>
								<div className="hero-btns">
									<Link className="btn enquire-btn" to="/admission">Apply Now</Link>
								</div>
							</motion.div>
						</div>
					</div>
				</div>

				<motion.section
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					viewport={{ once: true, margin: "-100px" }}
					transition={{ duration: 0.8 }}
				>
					<About />
				</motion.section>

				<motion.section
					initial={{ opacity: 0, y: 50 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.8 }}
				>
					<Path />
				</motion.section>

				<motion.section
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					viewport={{ once: true }}
					transition={{ duration: 0.8 }}
				>
					<Programes />
				</motion.section>

				<motion.section
					initial={{ opacity: 0, y: 40 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.8 }}
				>
					<Gallery />
				</motion.section>

				<motion.section
					initial={{ opacity: 0, scale: 0.95 }}
					whileInView={{ opacity: 1, scale: 1 }}
					viewport={{ once: true }}
					transition={{ duration: 0.8 }}
				>
					<Blog />
				</motion.section>

				<motion.div 
					className="student-dev-section"
					initial={{ opacity: 0, y: 50 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.8 }}
				>
					<div className="container-main">
						<div className="row">
							<div className="col-lg-12 text-center mb-5">
								<h5 className="section-small-label montserrat">Holistic Growth</h5>
								<h2 className="section-main-title montserrat">Student Development <span className="highlight-text">Focus</span></h2>
							</div>
							<div className="col-lg-3 col-md-6 col-6">
								<div className="dev-card">
									<div className="dev-icon"><i className="ri-user-star-line"></i></div>
									<h5>Leadership &amp; Confidence</h5>
									<p>Building strong, confident future leaders.</p>
								</div>
							</div>
							<div className="col-lg-3 col-md-6 col-6">
								<div className="dev-card">
									<div className="dev-icon"><i className="ri-mic-2-line"></i></div>
									<h5>Public Speaking</h5>
									<p>Communication skills for real-world success.</p>
								</div>
							</div>
							<div className="col-lg-3 col-md-6 col-6">
								<div className="dev-card">
									<div className="dev-icon"><i className="ri-road-map-line"></i></div>
									<h5>Career Clarity</h5>
									<p>Mentorship to define your career path early.</p>
								</div>
							</div>
							<div className="col-lg-3 col-md-6 col-6">
								<div className="dev-card">
									<div className="dev-icon"><i className="ri-heart-pulse-line"></i></div>
									<h5>Emotional Well-being</h5>
									<p>A supportive environment for mental wellness.</p>
								</div>
							</div>
						</div>
					</div>
				</motion.div>

				<motion.div 
					className="faq-section-main"
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					viewport={{ once: true }}
					transition={{ duration: 1 }}
				>
					<div className="container-main">
						<div className="row align-items-center">
							<div className="col-lg-5 mb-5 mb-lg-0">
								<div className="faq-left-panel">
									<h5 className="section-small-label">Got Questions?</h5>
									<h2 className="faq-main-title montserrat">Frequently<br />Asked Questions</h2>
									<p className="faq-left-desc">Find answers to the most common questions about admissions, programs, and life at Le Fille Global School.</p>
									<Link to="/contact" className="btn faq-contact-btn">Contact Us <i className="ri-arrow-right-line"></i></Link>
								</div>
							</div>
							<div className="col-lg-7">
								<div className="faq-items-wrap">
									{faqs.map((faq, idx) => (
										<motion.div
											className={`faq-card${openFaq === idx ? " faq-open" : ""}`}
											key={idx}
											onClick={() => toggleFaq(idx)}
											initial={{ opacity: 0, x: 20 }}
											whileInView={{ opacity: 1, x: 0 }}
											transition={{ delay: idx * 0.1 }}
											viewport={{ once: true }}
										>
											<div className="faq-card-header">
												<span className="faq-number">{String(idx + 1).padStart(2, "0")}</span>
												<h5 className="faq-question">{faq.q}</h5>
												<span className="faq-toggle-icon">
													<i className={openFaq === idx ? "ri-subtract-line" : "ri-add-line"}></i>
												</span>
											</div>
											{openFaq === idx && (
												<div className="faq-answer">
													<p>{faq.a}</p>
												</div>
											)}
										</motion.div>
									))}
								</div>
							</div>
						</div>
					</div>
				</motion.div>
			</div>
			<Footer />
		</div>
	)
}

export default HomePage
